import { captureException } from '@sentry/nextjs';
import PopupContent from '~/src/components/common-components/popup-content/popup-content';
import { TYPE_ERROR_ACTION } from '../constants';
import type { ExtendedAxiosResponse, InternalDataFieldInterface } from '~/src/models/types/axios-response.types';
import type { ErrorConfig, ErrorHandlerDependencies, ErrorMapping } from '../types/error-handlers.types';

const runEffectsBeforeAction = (
    errorConfig: ErrorConfig,
    error: unknown,
    dependencies: ErrorHandlerDependencies,
) => {
    const { beforeAction, analyticsCallback, captureException: shouldCaptureException, fieldError } =
        errorConfig.effects || {};

    beforeAction?.();
    analyticsCallback?.();

    fieldError && dependencies.setFieldError?.(fieldError);

    if (shouldCaptureException) {
        captureException(error);
    }
};

const runEffectsAfterAction = (errorConfig: ErrorConfig) => {
    errorConfig.effects?.afterAction?.();
};

export const createErrorHandler = (errorMapping: ErrorMapping, dependencies: ErrorHandlerDependencies) => {
    return (error: ExtendedAxiosResponse<InternalDataFieldInterface>) => {
        const { showPopup, setChipsData, queryKey, fallback } = dependencies;

        // eslint-disable-next-line no-console
        console.log(queryKey, error.response);

        const errorStatus = error?.response?.status;
        const errorConfigurator =
            errorStatus !== undefined && errorStatus in errorMapping
                ? errorMapping[errorStatus as keyof typeof errorMapping]
                : undefined;

        const errorConfig = errorConfigurator?.(error) || fallback(error);

        if (!errorConfig) {
            return;
        }

        runEffectsBeforeAction(errorConfig, error, dependencies);

        if (errorConfig.action.type === TYPE_ERROR_ACTION.popup && showPopup) {
            const { config, popupId, buttonAsLink, handlers } = errorConfig.action;
            const { subButtonAction, mainButtonAction, resetPopup, closeCallback } = dependencies;
            showPopup(
                <PopupContent
                    {...config}
                    popupId={popupId}
                    resetPopup={resetPopup || (() => {})}
                    closeCallback={handlers?.closeCallback || closeCallback}
                    buttonAsLink={buttonAsLink}
                    mainButtonAction={handlers?.mainButtonAction || mainButtonAction}
                    subButtonAction={handlers?.subButtonAction || subButtonAction}
                />,
            );
        }

        if (errorConfig.action.type === TYPE_ERROR_ACTION.chips && setChipsData) {
            setChipsData({ error: errorConfig.action.error, show: true });
        }

        runEffectsAfterAction(errorConfig);
    };
};